import { toast } from "sonner";
import { convertSupabaseErrorMessage } from "./supabase-errors";
import * as Sentry from "@sentry/nextjs";
import type { ErrorEvent } from "@sentry/nextjs";

export const PG_UNIQUE_VIOLATION = "23505";

export class AppError extends Error {
  description: string;

  constructor(title: string, description: string) {
    super(title);
    this.description = description;
  }
}

interface ApiError {
  status?: number;
  response?: {
    status?: number;
  };
}

export const isApiError = (error: unknown): error is ApiError => {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const candidate = error as Record<string, unknown>;

  if ("status" in candidate && typeof candidate.status === "number") {
    return true;
  }

  if (
    "response" in candidate &&
    typeof candidate.response === "object" &&
    candidate.response !== null
  ) {
    const response = candidate.response as Record<string, unknown>;
    if ("status" in response && typeof response.status === "number") {
      return true;
    }
  }

  return false;
};

export const IGNORED_STATUS_CODES = [401, 403, 404] as const;
export type IgnoredStatusCode = (typeof IGNORED_STATUS_CODES)[number];

function handleAppError(error: AppError) {
  toast.error(convertSupabaseErrorMessage(error), {
    description: convertSupabaseErrorMessage(error.description),
  });
  console.error(error.message + " " + error.description);
}

export function handleError(error: unknown) {
  if (error instanceof AppError) {
    handleAppError(error);
  } else if (error instanceof Error) {
    toast.error(convertSupabaseErrorMessage(error));
    console.error(error.message);
  } else if (typeof error === "string") {
    toast.error(convertSupabaseErrorMessage(error));
    console.error(error);
  } else {
    toast.error("An unknown error occurred.");
    console.error("An unknown error occurred.");
  }
  Sentry.captureException(error);
}

export function isHostAppInjectedError(event: ErrorEvent): boolean {
  const frames = event.exception?.values?.flatMap(
    (e) => e.stacktrace?.frames ?? []
  );
  return (
    frames?.some((frame) => frame.filename?.startsWith("app:///")) ?? false
  );
}

export function isTransitionAbortedError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    error.name === "InvalidStateError" &&
    error.message.includes("Transition was aborted")
  );
}

export function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException || error instanceof Error) &&
    error.name === "AbortError"
  );
}

export function isFirefoxInternalError(error: unknown): boolean {
  return error instanceof Error && error.name === "NS_ERROR_UNEXPECTED";
}

export function isThirdPartySentryError(
  error: unknown,
  event: ErrorEvent
): boolean {
  const errorMessage = error instanceof Error ? error.message : "";
  const initialExceptionValue = event.exception?.values?.[0]?.value ?? "";

  const frames = event.exception?.values?.flatMap(
    (e) => e.stacktrace?.frames ?? []
  );
  const hasNoAppFrames =
    !frames ||
    frames.length === 0 ||
    frames.every((frame) => !frame.filename || frame.filename === "undefined");
  const hasExtensionFrame = frames?.some(
    (frame) =>
      frame.filename?.includes("chrome-extension://") ||
      frame.filename?.includes("moz-extension://") ||
      frame.filename?.includes("inpage.js")
  );
  const looksThirdParty = hasNoAppFrames || hasExtensionFrame;

  // FILTER OUT PRODUCTION MINIFIED 2-LETTER NOISE (Aa, Vd, he, etc.)
  const isTwoLetterNoise =
    ((errorMessage.length === 2 && /^[A-Za-z]{2}$/.test(errorMessage)) ||
      (initialExceptionValue.length === 2 &&
        /^[A-Za-z]{2}$/.test(initialExceptionValue)) ||
      /^Non-Error promise rejection captured with value: [A-Za-z]{2}$/.test(
        initialExceptionValue
      )) &&
    looksThirdParty;

  if (isTwoLetterNoise) {
    return true;
  }

  // FILTER OUT THIRD-PARTY AD-TECH / MRAID WEBVIEW NOISE
  if (
    errorMessage.includes("mraidService") ||
    errorMessage.includes("mraid") ||
    initialExceptionValue.includes("mraidService") ||
    initialExceptionValue.includes("mraid is not defined")
  ) {
    return true;
  }

  // FILTER OUT WEBKIT / SENTRY NAVIGATION OVERFLOW NOISE

  if (
    (errorMessage.includes("Maximum call stack size exceeded") ||
      initialExceptionValue.includes("Maximum call stack size exceeded")) &&
    hasExtensionFrame
  ) {
    return true;
  }

  // FILTER OUT IN-APP BROWSER TELEMETRY NOISE (Instagram / Facebook Android)
  if (
    errorMessage.includes("Java object is gone") ||
    initialExceptionValue.includes("Java object is gone")
  ) {
    return true;
  }

  // Catch any other uncaught errors stemming directly from Meta's logger frame
  const hasMetaInjectedFrame = frames?.some((frame) =>
    frame.filename?.includes("navigation_performance_logger")
  );
  if (hasMetaInjectedFrame) {
    return true;
  }

  // SILENCE THE THIRD-PARTY STRING REJECTION NOISE
  if (typeof error === "string" && error === "Not found" && looksThirdParty) {
    return true;
  }

  // FILTER OUT BROWSER EXTENSION RUNTIME ERRORS
  // Check both the JavaScript object itself and the structured Sentry exception values
  if (
    errorMessage.includes("runtime.sendMessage") ||
    errorMessage.includes("Extension context invalidated") ||
    initialExceptionValue.includes("runtime.sendMessage")
  ) {
    return true;
  }

  // FILTER OUT THIRD-PARTY AUTOFILL / XBROWSER WEBVIEW NOISE
  if (
    errorMessage.includes("xbrowser") ||
    errorMessage.includes("execute_auto_fill") ||
    errorMessage.includes("swbrowser") ||
    errorMessage.includes("needInjectCss") ||
    initialExceptionValue.includes("xbrowser is not defined") ||
    initialExceptionValue.includes("swbrowser is not defined")
  ) {
    return true;
  }

  // FILTER OUT CRYPTO WALLET CONTENT INJECTION NOISE (Phantom, TronLink, etc.)
  // The generic "code, message" shape alone also matches first-party rejected
  // API/SDK objects, so only trust it alongside a wallet extension frame.
  if (
    (initialExceptionValue.includes(
      "Object captured as promise rejection with keys: code, message"
    ) &&
      hasExtensionFrame) ||
    errorMessage.includes("window.phantom") ||
    errorMessage.includes("TronLink")
  ) {
    return true;
  }

  // FILTER OUT BROWSER EXTENSION STORAGE QUOTA NOISE

  if (
    errorMessage.includes("browser.storage.local") ||
    initialExceptionValue.includes("browser.storage.local") ||
    (errorMessage.includes("Exceeded storage quota") && hasExtensionFrame)
  ) {
    return true;
  }

  return false;
}
